import { useEffect, useState } from "react";
import { AtSign, BadgeCheck, Bell, CheckCheck, Flag, MessageCircle, PlayCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BACKEND, channelApi, errText } from "@/lib/api";

const iconFor = (kind) => {
  if (kind === "mention") return AtSign;
  if (kind === "new_video") return PlayCircle;
  if (kind === "report_update") return Flag;
  return MessageCircle;
};

const notificationDate = (value) => value ? new Date(value).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "Just now";

export const ChannelNotifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const load = () => channelApi.get("/customer/notifications").then(({ data }) => {
      if (!active) return;
      setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
      setUnreadCount(data.unread_count || 0);
      setError("");
    }).catch((e) => { if (active) setError(errText(e)); });
    const handleCreated = (event) => {
      const notification = event.detail;
      if (!notification?.id) return;
      setNotifications((current) => [notification, ...current.filter((item) => item.id !== notification.id)].slice(0, 100));
      if (!notification.read) setUnreadCount((current) => current + 1);
    };
    load();
    const timer = setInterval(load, 30000);
    window.addEventListener("channel-notifications-refresh", load);
    window.addEventListener("channel-notification-created", handleCreated);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener("channel-notifications-refresh", load);
      window.removeEventListener("channel-notification-created", handleCreated);
    };
  }, []);

  const markRead = async (notification) => {
    if (notification.read) return;
    setNotifications((current) => current.map((item) => item.id === notification.id ? { ...item, read: true } : item));
    setUnreadCount((current) => Math.max(0, current - 1));
    try {
      await channelApi.post(`/customer/notifications/${notification.id}/read`);
    } catch (e) {
      setError(errText(e));
    }
  };

  const openNotification = async (notification) => {
    await markRead(notification);
    setOpen(false);
    if (notification.target_path) navigate(notification.target_path);
  };

  const markAllRead = async () => {
    setNotifications((current) => current.map((item) => ({ ...item, read: true })));
    setUnreadCount(0);
    try {
      await channelApi.post("/customer/notifications/read-all");
    } catch (e) {
      setError(errText(e));
    }
  };

  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((current) => !current)} className="relative inline-flex h-10 w-10 items-center justify-center text-white/70 transition-colors hover:text-white" aria-label={unreadCount ? `Notifications, ${unreadCount} unread` : "Notifications"} aria-expanded={open} data-testid="channel-notifications-toggle">
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && <span className="absolute right-0 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#E62314] px-1 text-[9px] font-bold text-white" data-testid="channel-notification-count">{unreadCount > 99 ? "99+" : unreadCount}</span>}
      </button>
      {open && <>
        <button type="button" onClick={() => setOpen(false)} className="fixed inset-0 z-30 cursor-default" aria-label="Close notifications" />
        <section className="fixed left-3 right-3 top-[10.5rem] z-50 max-h-[calc(100dvh-11.25rem)] w-auto overflow-hidden border border-white/15 bg-[#111111] shadow-2xl sm:left-auto sm:right-6 sm:w-[min(22rem,calc(100vw-3rem))] lg:absolute lg:left-auto lg:right-0 lg:top-12 lg:max-h-none lg:w-[min(22rem,calc(100vw-1.5rem))]" aria-label="Notifications" data-testid="channel-notifications-panel">
          <header className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <h2 className="font-display text-base font-bold">Notifications</h2>
            {unreadCount > 0 && <button type="button" onClick={markAllRead} className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/55 hover:text-white"><CheckCheck className="h-3.5 w-3.5" />Mark all read</button>}
          </header>
          {error && <p className="px-4 py-2 text-xs text-[#FF8B7F]" role="alert">{error}</p>}
          {notifications.length ? <ul className="max-h-[calc(100dvh-15rem)] divide-y divide-white/10 overflow-y-auto lg:max-h-[min(70vh,28rem)]">
            {notifications.map((notification) => {
              const Icon = iconFor(notification.kind);
              const action = notification.actor_name && notification.message?.startsWith(`${notification.actor_name} `)
                ? notification.message.slice(notification.actor_name.length + 1)
                : notification.message;
              const isCommentRemoval = notification.kind === "comment_removed";
              return <li key={notification.id}>
                <button type="button" onClick={() => openNotification(notification)} className={`flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-white/[0.05] ${notification.read ? "" : "bg-white/[0.025]"}`} data-testid={`channel-notification-${notification.id}`}>
                  <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${notification.read ? "text-white/45" : "text-[#FF8B7F]"}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm leading-5 text-white/85">
                      {isCommentRemoval ? <>
                        <span className="mb-2 flex items-center gap-2 font-semibold" data-testid={`channel-comment-removal-actor-${notification.id}`}>
                          {notification.actor_avatar_media_id ? <img src={`${BACKEND}/api/media/${notification.actor_avatar_media_id}/file`} alt="" className="h-8 w-8 rounded-full object-cover" /> : <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-xs" aria-hidden="true">CL</span>}
                          <span>{notification.actor_name || "Callum Lamb"}{notification.actor_is_verified && <BadgeCheck className="ml-1 inline h-3.5 w-3.5 fill-sky-500 text-white" aria-label="Verified account" />}{notification.actor_username && <span className="ml-1 text-xs font-normal text-white/45">@{notification.actor_username}</span>}</span>
                        </span>
                        {notification.message && <span className="mb-2 block"><strong>Message from {notification.actor_name || "Callum Lamb"}:</strong> {notification.message}</span>}
                        <span className="block font-semibold">This comment was removed:</span>
                        <blockquote className="mt-1 whitespace-pre-wrap border-l border-white/20 pl-3 text-white/70" data-testid={`channel-comment-removal-text-${notification.id}`}>{notification.removed_comment_text}</blockquote>
                      </> : <>
                        {notification.actor_name && <span className="mr-1 inline-flex items-center gap-0.5 font-semibold">{notification.actor_name}{notification.actor_is_verified && <BadgeCheck className="h-3.5 w-3.5 shrink-0 fill-sky-500 text-white" aria-label="Verified account" />}</span>}
                        {notification.actor_username && <span className="mr-1 text-xs text-white/45">@{notification.actor_username}</span>}
                        {notification.kind === "moderation" && !notification.actor_name
                          ? (notification.message || notification.title)
                          : action}
                        {notification.recipient_username && <span className="ml-1 text-xs text-white/45">@{notification.recipient_username}</span>}
                      </>}
                    </span>
                    <time className="mt-1 block text-[11px] text-white/40" dateTime={notification.created_at}>{notificationDate(notification.created_at)}</time>
                  </span>
                  {!notification.read && <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#E62314]" aria-label="Unread" />}
                </button>
              </li>;
            })}
          </ul> : <p className="px-4 py-8 text-center text-sm text-white/50">You’re all caught up.</p>}
        </section>
      </>}
    </div>
  );
};